"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Gauge } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SolutionItem {
  id: string;
  title: string;
  badge: string;
  description: string;
  iconName: "activity" | "gauge";
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  linkPath: string;
  bulletPoints: string[];
}
const solutionsData: SolutionItem[] = [{
  id: "pat",
  title: "Process Analytical Technology",
  badge: "Real-time Monitoring",
  description: "Integrated in-line and at-line analytical instrumentation engineered for continuous measurement of critical process parameters across pharmaceutical manufacturing.",
  iconName: "activity",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology.webp-wvc-sizes",
  imageAlt: "Process analytical technology equipment in cleanroom laboratory",
  linkPath: "/SolutionsPage",
  bulletPoints: ["Continuous in-line spectroscopic monitoring", "Regulatory compliant data integrity workflows", "Immediate detection of batch variations", "Validated integration with existing control loops"]
}, {
  id: "drying",
  title: "Drying Endpoint",
  badge: "Process Optimization",
  description: "Precision moisture sensing and automated endpoint determination systems designed to prevent over-drying, protect product integrity, and reduce cycle times.",
  iconName: "gauge",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_solution.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_solution.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_solution.png-wvc-sizes",
  imageAlt: "Drying endpoint analytical system during active process testing",
  linkPath: "/SolutionsPage",
  bulletPoints: ["Direct residual moisture measurement", "Automated cycle completion triggers", "Consistent product yield and lower energy usage", "Robust hygienic design for regulated suites"]
}];
export default function Solutions() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="keysolutionsoverview" data-nav="light" className="bg-background py-20 lg:py-28">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-2xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Core Capabilities
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
            Key Application Solutions
          </h2>
          <p className="text-muted-foreground text-base md:text-lg">
            Purpose-built analytical solutions that bring scientific precision and reliable compliance to regulated manufacturing lines.
          </p>
        </div>

        {/* 2-Column Solutions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {solutionsData.map((solution, i) => <motion.div key={solution.id} data-index={i} className="flex flex-col h-full" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.5,
          delay: i * 0.15
        }}>
              <Card className="flex flex-col h-full bg-card border border-border rounded-xl shadow-md overflow-hidden hover:border-primary/40 hover:shadow-lg transition-[border-color,box-shadow] duration-300">
                {/* Image Showcase */}
                <div className="relative w-full h-56 md:h-64 overflow-hidden bg-muted">
                  <img src={solution.imageSrc} data-wvc-srcset={solution.imageSrcset} data-wvc-sizes={solution.imageSizes} alt={solution.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary" className="bg-background/90 text-foreground border border-border backdrop-blur-xs font-medium px-3 py-1 shadow-xs">
                      {solution.badge}
                    </Badge>
                  </div>
                </div>

                {/* Card Header */}
                <CardHeader className="pt-6 pb-2">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center justify-center size-10 rounded-lg bg-primary/10 text-primary">
                      {solution.iconName === "activity" ? <Activity className="size-5" /> : <Gauge className="size-5" />}
                    </div>
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                      Analytical System
                    </span>
                  </div>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {solution.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm md:text-base leading-relaxed pt-2">
                    {solution.description}
                  </CardDescription>
                </CardHeader>

                {/* Card Content - Feature List */}
                <CardContent className="flex-1 py-4">
                  <div className="border-t border-border pt-4">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                      Key Capabilities & Benefits
                    </p>
                    <ul className="space-y-2.5">
                      {solution.bulletPoints.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </div>
                </CardContent>

                {/* Card Footer - Action Button */}
                <CardFooter className="pt-2 pb-6 border-t border-border/60">
                  <Button asChild variant="default" className="w-full sm:w-auto font-medium">
                    <Link to={solution.linkPath}>
                      Explore Solution
                      <ArrowRight className="size-4 ml-1.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}